<script lang="ts" setup>
import { useId, useTemplateRef } from "#imports";
import type { InputProps } from "./types";
import useValidation from "./useValidation";

const modelValue = defineModel<string | null>({ required: true });

const props = withDefaults(
  defineProps<
    InputProps & {
      type?: "text" | "email" | "tel" | "url" | "password";
      placeholder?: string;
    }
  >(),
  {
    type: "text",
    placeholder: undefined,
  },
);

defineSlots<{
  helpText(): void;
}>();

const elementId = `input-${useId()}`;
const input = useTemplateRef("input");

const { expose, isTouched, error } = useValidation({
  input,
  modelValue,
  props,
});
defineExpose(expose);
</script>

<template>
  <ClientOnly>
    <div :class="{ 'input-string': true, 'is-touched': isTouched }">
      <label v-if="!!props.label" :for="elementId">
        {{ props.label }}
      </label>
      <div class="input-wrapper">
        <input
          ref="input"
          v-model="modelValue"
          :id="elementId"
          :type="props.type"
          :name="props.name"
          :placeholder="props.placeholder"
          :readonly="props.isReadonly"
          :disabled="props.isDisabled"
        />
        <i
          v-if="props.validate && !error && isTouched"
          class="ri-checkbox-circle-line valid"
        ></i>
      </div>
      <small v-if="$slots.helpText" class="helper-text"
        ><slot name="helpText"></slot
      ></small>
      <small v-if="isTouched && error" class="error-message">{{ error }}</small>
    </div>
  </ClientOnly>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/input.pcss";

.input-string {
  width: 100%;
  display: grid;
  text-align: left;
}

label {
  @mixin mp-input-label;
}

.input-wrapper {
  position: relative;
}

input {
  @mixin mp-input;
  font-size: 1rem;
}

input::placeholder {
  color: var(--color-slate-400);
  font-family: "Hanken Grotesk";
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
}

input:disabled,
input:read-only {
  opacity: 0.6;
  cursor: not-allowed;
}

.is-touched input:invalid {
  @mixin mp-input-invalid;
}

i.valid {
  position: absolute;
  right: 0.65rem;
  top: 0.9rem;
  z-index: 10;
  color: var(--color-green-400);
}

.helper-text {
  @mixin mp-helper-text;
  display: block;
}

.error-message {
  @mixin mp-error-text;
  display: block;
}
</style>
